import { labArt, labReady } from './lab/index.js';
import type { Ctx, GardenArt } from './types.js';

/** The art the garden uses: the Thicket RPG Lab's. Dev mode can switch to the old placeholder with `useArt`. */
export let art: GardenArt = labArt;

const icons = new Map<string, string>();

/** Switches the art everywhere (dev mode compares the Lab's art with the placeholder). */
export function useArt(next: GardenArt) {
  art = next;
  icons.clear();
}

/** A small picture drawn with the current art, cached by `key`, for chips and lists. */
export function icon(key: string, draw: (ctx: Ctx) => void, w = 24, h = 24): string {
  const k = `${art.name}:${key}:${w}x${h}`;
  let url = icons.get(k);
  if (!url) {
    const scale = Math.max(2, Math.round(48 / w));
    const c = document.createElement('canvas');
    c.width = w * scale;
    c.height = h * scale;
    const ctx = c.getContext('2d')!;
    ctx.imageSmoothingEnabled = false;
    ctx.scale(scale, scale);
    draw(ctx);
    url = c.toDataURL();
    // Until the Lab has loaded, its sprites draw nothing: don't keep a blank picture.
    if (art !== labArt || labReady()) icons.set(k, url);
  }
  return url;
}

/**
 * Like `icon`, but cropped to the pixels actually drawn and scaled up by whole pixels to fill a
 * square `out` pixels wide, so a seed or bud fills its thumbnail as fully as a grown tree does.
 * Tiny sprites stop short of the edge so they don't turn into blocks.
 */
export function iconFit(key: string, draw: (ctx: Ctx) => void, w = 32, h = 32, out = 112): string {
  const k = `${art.name}:fit:${key}:${w}x${h}:${out}`;
  let url = icons.get(k);
  if (url) return url;
  const src = document.createElement('canvas');
  src.width = w;
  src.height = h;
  const sctx = src.getContext('2d')!;
  sctx.imageSmoothingEnabled = false;
  draw(sctx);
  const { data } = sctx.getImageData(0, 0, w, h);
  let x0 = w, y0 = h, x1 = -1, y1 = -1;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (data[(y * w + x) * 4 + 3]! > 8) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
  }
  const c = document.createElement('canvas');
  c.width = c.height = out;
  if (x1 >= 0) {
    const bw = x1 - x0 + 1, bh = y1 - y0 + 1, big = Math.max(bw, bh);
    const scale = Math.max(1, Math.floor((big < 12 ? out * 0.72 : out) / big));
    const ctx = c.getContext('2d')!;
    ctx.imageSmoothingEnabled = false;
    // Centred across, sitting on the bottom like it's planted.
    const dw = bw * scale, dh = bh * scale;
    const dy = big < 12 ? Math.round((out - dh) / 2 + dh * 0.15) : out - dh;
    ctx.drawImage(src, x0, y0, bw, bh, Math.round((out - dw) / 2), Math.min(out - dh, dy), dw, dh);
  }
  url = c.toDataURL();
  if (x1 >= 0 && (art !== labArt || labReady())) icons.set(k, url);
  return url;
}
